import { Trans, useLingui } from '@lingui/react/macro';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { BaseModal } from '@/components/ui/base-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

/**
 * AI-name optimisation modal (M6). Shows the current template name,
 * runs the supplied `generate` callback when the user clicks
 * **Generate**, and surfaces the suggestion in an editable field.
 * Accept fires `onAccept(suggested)`; cancel fires `onCancel`.
 *
 * The modal is intentionally decoupled from the underlying AI call —
 * callers pass `generate` so Step 1 can wire it to
 * `optimizeTemplateNameStep1` (basic context) while Step 4 wires to
 * `optimizeTemplateNameStep4` (full template context).
 */
export function OptimizeNameModal({
  currentName,
  generate,
  onAccept,
  onCancel,
}: OptimizeNameModalProps) {
  const { t } = useLingui();
  const [suggested, setSuggested] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Run the caller-supplied AI thunk and stash the suggestion. Surfaces
   * a copy-friendly error if the request fails so the user can retry
   * without leaving the modal.
   */
  async function handleGenerate() {
    setBusy(true);
    setError(null);
    try {
      const result = await generate();
      setSuggested(result);
    } catch {
      setError(t`Couldn't generate a suggestion. Try again in a moment.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <BaseModal
      open
      onOpenChange={(open) => !open && onCancel()}
      title={<Trans>Optimise template name</Trans>}
      description={<Trans>AI-suggest a clearer name based on what you've entered so far.</Trans>}
      className="sm:max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={() => onAccept(suggested)} disabled={!suggested}>
            <Trans>Accept</Trans>
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="optimize-current-name">
            <Trans>Current name</Trans>
          </Label>
          <Input id="optimize-current-name" value={currentName} disabled readOnly />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="optimize-suggested-name">
              <Trans>Suggested name</Trans>
            </Label>
            <Button variant="outline" size="sm" onClick={handleGenerate} disabled={busy}>
              {busy ? (
                <>
                  <Spinner className="mr-2 h-3 w-3" />
                  <Trans>Generating…</Trans>
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-3 w-3" />
                  <Trans>Generate</Trans>
                </>
              )}
            </Button>
          </div>
          <Input
            id="optimize-suggested-name"
            value={suggested}
            onChange={(e) => setSuggested(e.target.value)}
            placeholder={t`Press Generate to get a suggestion`}
          />
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
    </BaseModal>
  );
}

interface OptimizeNameModalProps {
  currentName: string;
  generate: () => Promise<string>;
  onAccept: (name: string) => void;
  onCancel: () => void;
}
